import { Slot } from 'radix-ui'
import type * as React from 'react'

import { cn } from '../../lib/utils'

/**
 * A status with a word (DESIGN.md "Status badges").
 *
 * 22px tall, 3px corners, 12px weight-500 sentence-case text and a 6px SQUARE
 * pip in the text colour. The pip is square because the rhombus belongs to the
 * nuqta alone ("The One Rhombus Rule"); a red rhombus pip would read as "you
 * are here".
 *
 * | Prop | Question it answers |
 * |---|---|
 * | `tone` | Which signal? |
 * | `fill` | Is it read (`outline`, the soft tint), or is it an alarm (`solid`)? |
 *
 * `tone` is named for the signal rather than for POSTED or DRAFT, because
 * @multaqa/ui carries no domain meaning. The module that owns a document owns
 * the mapping from its status to a tone.
 *
 * - `outline` is the historical name of the SOFT fill: the signal's own tint
 *   (`--success-subtle` and so on) with its signal text, no border. Every tone
 *   defaults to it, `danger` included: rejected, cancelled and void are
 *   statuses, not alarms.
 * - `solid` is reserved for danger alarms such as overdue or blocked, and for
 *   counts: `--danger` with `--danger-foreground` and no pip, because the
 *   block is already the colour. Pass `fill="solid"` on purpose.
 * - `signal` was the old brand-red highlight ("default", "approved",
 *   "converted"). Red is never a status now ("The Red Is Never an Action
 *   Rule"), so it draws in grey ink, `--brand-subtle`.
 */

type BadgeTone = 'neutral' | 'success' | 'warning' | 'info' | 'danger' | 'signal'
type BadgeFill = 'outline' | 'solid'

const TONE_SOFT: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-subtle text-muted-foreground',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  info: 'bg-info-subtle text-info',
  danger: 'bg-danger-subtle text-danger',
  signal: 'bg-brand-subtle text-brand-subtle-foreground',
}

/**
 * Only `danger` is designed solid. The other tones keep a solid face so the
 * prop stays total; each puts paper text on the full signal, never a
 * lightened one ("The Signal-on-Card Rule").
 */
const TONE_SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-muted-foreground text-card',
  success: 'bg-success text-card',
  warning: 'bg-warning text-card',
  info: 'bg-info text-card',
  danger: 'bg-danger text-danger-foreground',
  signal: 'bg-primary text-primary-foreground',
}

/** The shape every badge shares. Kept as a function for the old cva call sites. */
const badgeVariants = () =>
  cn(
    'inline-flex h-[22px] w-fit shrink-0 items-center gap-1.5 whitespace-nowrap align-middle',
    'rounded-badge px-2 font-sans text-xs leading-none font-medium',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3",
    '[a&]:hover:underline',
  )

function Badge({
  className,
  tone = 'neutral',
  fill = 'outline',
  asChild = false,
  children,
  ...props
}: React.ComponentProps<'span'> & {
  tone?: BadgeTone
  fill?: BadgeFill
  asChild?: boolean
}) {
  const Comp = asChild ? Slot.Root : 'span'
  const isSolid = fill === 'solid'

  return (
    <Comp
      data-slot="badge"
      data-tone={tone}
      data-fill={fill}
      className={cn(
        badgeVariants(),
        isSolid ? TONE_SOLID[tone] : ['ps-[7px]', TONE_SOFT[tone]],
        className,
      )}
      {...props}
    >
      {/* The pip is the word's shape partner: the soft tint alone is a hue
          difference, and no meaning may rest on colour alone. */}
      {isSolid ? null : (
        <span data-slot="badge-pip" className="size-1.5 shrink-0 bg-current" aria-hidden="true" />
      )}
      {asChild ? <Slot.Slottable>{children}</Slot.Slottable> : children}
    </Comp>
  )
}

export { Badge, type BadgeFill, type BadgeTone, badgeVariants }
